import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Search as SearchIcon, Save as SaveIcon } from '@mui/icons-material';
import { useMasterData } from '../../../hooks/useMasterData';
import { subjectAssignApi, SubjectAssignStudent, SubjectAssignResponse } from './subjectAssignApi';
import SubjectAssignTable from './SubjectAssignTable';

const SubjectAssignSubjectWise: React.FC = () => {
  const { masters, fetchMasters, fetchSections: fetchSectionsHook, fetchAcademicSessions } = useMasterData();
  
  // Filter state
  const [filters, setFilters] = useState({
    sessionId: '',
    classId: '',
    sectionId: '',
    subjectId: '',
  });
  
  // Data state
  const [students, setStudents] = useState<SubjectAssignStudent[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Selection state
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  
  // UI state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    if (filters.classId) {
      (async () => {
        const [secs, subs] = await Promise.all([
          fetchSectionsHook(filters.classId),
          subjectAssignApi.getSubjectsByClass(filters.classId)
        ]);
        setSections(secs);
        setSubjects(subs);
      })();
    } else {
      setSections([]);
      setSubjects([]);
    }
  }, [filters.classId]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    if (field === 'classId') {
      setFilters((prev) => ({ ...prev, sectionId: '', subjectId: '' }));
    }
  };

  const handleLoadStudents = async () => {
    if (!filters.sessionId || !filters.classId || !filters.sectionId) {
      setSnackbar({ open: true, message: 'Please select session, class and section', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const data = await subjectAssignApi.loadStudents({
        sessionId: filters.sessionId,
        classId: filters.classId,
        sectionId: filters.sectionId
      });
      setStudents(data);
      setSelectedStudents([]);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to load students', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (studentId: string) => {
    const currentIndex = selectedStudents.indexOf(studentId);
    const newSelected = [...selectedStudents];

    if (currentIndex === -1) {
      newSelected.push(studentId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedStudents(newSelected);
  };

  const handleToggleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = students.map((n) => n.id);
      setSelectedStudents(newSelecteds);
      return;
    }
    setSelectedStudents([]);
  };

  const handleAssignSubject = async () => {
    if (!filters.subjectId) {
      setSnackbar({ open: true, message: 'Please select a subject to assign', severity: 'error' });
      return;
    }

    if (selectedStudents.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one student', severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        subjectId: filters.subjectId,
        academicSessionId: filters.sessionId,
        classId: filters.classId,
        sectionId: filters.sectionId,
        students: selectedStudents,
      };

      const result: SubjectAssignResponse = await subjectAssignApi.assignSubject(payload);
      
      const subjectName = subjects.find(s => s.id === filters.subjectId)?.name || 'Subject';
      setSnackbar({ 
        open: true, 
        message: `${result.assigned} students assigned ${subjectName} successfully. ${result.skipped} skipped (already assigned).`, 
        severity: 'success' 
      });
      
      // Optional: Clear selection after success
      setSelectedStudents([]);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Assignment failed', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedClassName = masters.classes?.find(c => c.id === filters.classId)?.name || '';
  const selectedSectionName = sections.find(s => s.id === filters.sectionId)?.name || '';

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 3 }}>
        <Typography variant="h4" gutterBottom>Subject Assign (Subject Wise)</Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Filter Students & Select Subject</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Academic Session"
                value={filters.sessionId}
                onChange={(e) => handleFilterChange('sessionId', e.target.value)}
              >
                {masters.academicSessions?.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Class"
                value={filters.classId}
                onChange={(e) => handleFilterChange('classId', e.target.value)}
              >
                {masters.classes?.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Section"
                disabled={!filters.classId}
                value={filters.sectionId}
                onChange={(e) => handleFilterChange('sectionId', e.target.value)}
              >
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <TextField
                select
                fullWidth
                label="Subject"
                disabled={!filters.classId}
                value={filters.subjectId}
                onChange={(e) => handleFilterChange('subjectId', e.target.value)}
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Button
                variant="contained"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleLoadStudents}
                disabled={loading}
              >
                Load Students
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {students.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Assign {subjects.find(s => s.id === filters.subjectId)?.name || 'Subject'}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleAssignSubject}
                disabled={submitting || selectedStudents.length === 0 || !filters.subjectId}
              >
                Assign Subject to Selected ({selectedStudents.length})
              </Button>
            </Box>

            <SubjectAssignTable
              students={students}
              selectedStudents={selectedStudents}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              className={selectedClassName}
              sectionName={selectedSectionName}
            />
          </Paper>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SubjectAssignSubjectWise;
